import { queryOptions } from '@tanstack/react-query'
import { apiExchange, apiGet, apiPost, apiPostForm, type PostValue } from './client'

/**
 * Contractul consolei CMDB (v4/api, App\Cmdb\*).
 *
 * Numele campurilor sunt exact cele pe care le scrie serverul. Cusatura dintre
 * cele doua jumatati s-a rupt de trei ori saptamana asta - fiecare parte se
 * proba pe sine si amandoua treceau - deci forma raspunsului e inghetata intr-o
 * inregistrare comuna (`src/cmdb/__fixtures__/`), citita si de proba din
 * browser, si de cea din PHP.
 */

/** Ce se poate trimite intr-un POST - inclusiv cheile `field[<id>]`. */
export type PostValueMap = Record<string, PostValue>

/** O pereche numar-eticheta dintr-un vocabular (stari, medii, importanta). */
export interface Pair {
  value: number
  label: string
}

export interface CiClass {
  id: number
  name: string
  kind: number
}

/**
 * O fila din banda din capul ecranelor CMDB - `inventoree/_views.php`.
 *
 * `url` e calea din portal, intreaga (`cmdb`, `cmdb/health`, `maintenance`):
 * cele patru stau sub doua controllere de nivel intai, deci nu se pot construi
 * dintr-un prefix. Ecranul ii pune `/app` in fata.
 *
 * Lista vine FILTRATA de pe server, si de doua ori - pe drept si pe ce s-a
 * mutat deja in v4. Vezi `App\Cmdb\CmdbViews`.
 */
export interface CmdbView {
  url: string
  icon: string
  label: string
  note: string
}

export interface CmdbShell {
  classes: CiClass[]
  kinds: Pair[]
  statuses: Pair[]
  environments: Pair[]
  criticality: Pair[]
  canEdit: boolean
  canDelete: boolean
  canReadHealth: boolean
  views: CmdbView[]
}

/** Un rand din lista - `Cmdb::data()`. */
export interface CiRow {
  DT_RowId: string
  ID: number
  name: string
  class_name: string
  kind: number
  kind_label: string
  environment: number
  environment_label: string
  status: number
  status_label: string
  status_pill: string
  criticality: number | null
  criticality_label: string
  health: number
  health_label: string
  health_pill: string
  relations: number
  serial: string | null
  last_seen: string | null
  /** Facut de mana: se poate si retrage. Venit din descoperire: numai edita. */
  manual: boolean
}

export interface CiTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: CiRow[]
}

export interface CiTableRequest {
  draw: number
  start: number
  length: number
  search: string
  ci_class_id: number
  kind: number
  status: string
  environment: string
  unhealthy: boolean
  order: { column: number; dir: 'asc' | 'desc' }
}

export function cmdbTableParams(r: CiTableRequest): URLSearchParams {
  const params = new URLSearchParams()
  params.set('draw', String(r.draw))
  params.set('start', String(r.start))
  params.set('length', String(r.length))
  params.set('search[value]', r.search)
  params.set('ci_class_id', String(r.ci_class_id))
  params.set('kind', String(r.kind))
  params.set('status', r.status)
  params.set('environment', r.environment)
  params.set('unhealthy', r.unhealthy ? '1' : '0')
  params.set('order[0][column]', String(r.order.column))
  params.set('order[0][dir]', r.order.dir)
  return params
}

export interface Importance {
  level: number
  label: string
  class: string
}

export interface CiHeader {
  id: number
  name: string
  className: string
  classId: number
  kind: number
  kindLabel: string
  companyId: number
  companyName: string
  status: number
  statusLabel: string
  statusPill: string
  health: number
  healthLabel: string
  healthPill: string
  environment: number
  environmentLabel: string
  importance: Importance
  criticality: number | null
  refType: number
  refId: number
  manual: boolean
  deviceName: string
  serial: string | null
  osVersion: string | null
  lastUser: string | null
  acquisitionDate: string | null
  warrantyExpiry: string | null
  extendedWarrantyExpiry: string | null
  lastSeen: string | null
  firstSeen: string | null
  verifiedAt: string | null
}

export interface CiFieldOption {
  id: number
  label: string
  value: string
}

/** O proprietate a clasei - `it_ci_class_fields` plus valoarea ei. */
export interface CiField {
  id: number
  key: string
  label: string
  type: number
  typeLabel: string
  required: boolean
  value: string | number | null
  options: CiFieldOption[]
}

export interface Owner {
  id: number
  name: string | null
  missing: boolean
  inheritedFrom: { id: number; name: string; depth: number } | null
}

export interface Ownership {
  business: Owner | null
  technical: Owner | null
}

export interface CiEdge {
  id: number
  direction: 'in' | 'out'
  relation: string
  itemId: number
  itemName: string
  itemClass: string
  origin: string
}

export interface CiImpactRow {
  id: number
  name: string
  className: string
  depth: number
  importance: Importance
  /** `it_ci_classes.kind` - de la 3 in sus e un serviciu, ca in `row_impact.php`. */
  kind: number
  /** 1 = productie; 0 = nu se stie, si „nu se stie" nu e „nu e productie". */
  environment: number
  /** Costul pe luna din catalog (Service_costs::monthlyForCis); null = nu e in catalog. */
  cost: { amount: number; currency: string } | null
}

/** "Among them, N catalog service(s), worth X a month in total". */
export interface CiImpactPriced {
  count: number
  totals: Record<string, number>
  top: { id: number; name: string; amount: number; currency: string }[]
}

export interface CiTicket {
  id: number
  subject: string
  opened: string
  state: { label: string; pill: string; resolved: string | null }
}

export interface CiAuditRow {
  when: string
  who: string
  what: string
  from: string
  to: string
}

export interface CiRecordPage {
  ci: CiHeader
  fields: CiField[]
  ownership: Ownership
  canWrite: boolean
  canDelete: boolean
  relationTypes: { id: number; name: string }[]
  outgoing: CiEdge[]
  incoming: CiEdge[]
  impact: CiImpactRow[]
  tickets: CiTicket[]
  impactPriced?: CiImpactPriced
  ticketLimit: number
  openTickets: number
  audit: CiAuditRow[]
  /** „Who says so" - de unde vine fiecare valoare, si care e tinuta in cui. */
  provenance: ProvenanceCard
  /** `null` = omul n-are dreptul catalogului, si atunci sectiunea nu se deseneaza. */
  costs: CiCosts | null
  /** Ce a observat releul - `Ci_telemetry`, partea de pe fisa. */
  telemetry: CiTelemetry
  /** Ce a gasit scanarea - numai pentru un CI de aparat descoperit (`refType` 2). */
  scan?: CiScan | null
  /** Ce stie inventarul agentului - numai pentru un CI de program (`refType` 3). */
  software?: CiSoftware | null
}

/* ------------------------------------------------------------------------ *
 * Ce stie inventarul - `SoftwareCiCard`, numai pentru `ref_type` 3
 * ------------------------------------------------------------------------ */

/** O masina pe care sta programul: ID-ul de inventar si fisa ei CI, cand o are. */
export interface SoftwareMachine {
  id: number
  name: string
  ciId: number | null
}

export interface CiSoftware {
  /** false = randul de instalare nu mai e; ce se stia vine din `attrs`. */
  found: boolean
  note: string
  program: {
    name: string
    version: string | null
    publisher: string | null
    /** Cea mai noua versiune vazuta de catalog pe flota furnizorului. */
    latestVersion: string | null
    installed: string | null
    firstSeen: string | null
    /** Cand l-a raportat ultima oara agentul masinii. */
    lastReported: string | null
    sizeBytes: number | null
    category: string | null
    /** De ce a intrat in CMDB, scris la promovare. */
    why: string | null
  }
  /** null cand randul de instalare nu mai e; false = agentul nu-l mai raporteaza. */
  stillInstalled: boolean | null
  machine: (SoftwareMachine & { removed: boolean }) | null
  /** Acelasi program pe ALTE masini ale firmei. */
  elsewhere: {
    total: number
    machines: (SoftwareMachine & { version: string | null })[]
    limit: number
  }
}

export interface CiFormCi {
  id: number
  name: string
  classId: number
  companyId: number
  status: number
  criticality: number | null
  environment: number
  ownerUserId: number
  technicalOwnerId: number
  manual: boolean
}

export interface Person {
  id: number
  name: string
}

export interface CiFormPage {
  ci: CiFormCi | null
  classes: CiClass[]
  fields: CiField[]
  companies: Person[]
  /** Cmdb::form: ecranul din care s-a venit (registrul Domains) stie deja clientul. */
  hideCompany?: boolean
  statuses: Pair[]
  criticality: Pair[]
  environments: Pair[]
  contacts: Person[]
  staff: Person[]
  hasTechnical: boolean
}

export interface CmdbHealthPage {
  summary: Record<string, number | boolean>
  kinds: string[]
  canAttest: boolean
  views: CmdbView[]
}

export interface CmdbHealthList {
  recordsTotal: number
  recordsFiltered: number
  data: Record<string, unknown>[]
}

export interface WriteResult {
  status: 'OK' | 'ERROR'
  message: string
  id?: number
}

/** O fotografie a registrului - `it_ci_baseline`. */
export interface Baseline {
  id: number
  name: string
  note: string | null
  takenAt: string
  scopeCiId: number
  /** Gol inseamna "tot ce era atunci", nu "nimic". */
  scopeName: string | null
  ciCount: number
  edgeCount: number
}

export interface BaselineService {
  id: number
  name: string
  companyId: number
  kind: number
  className: string
}

export interface BaselinesPage {
  hasTables: boolean
  baselines: Baseline[]
  services: BaselineService[]
  canWrite: boolean
  /** Banda din capul ecranelor CMDB, filtrata pe server - `App\Cmdb\CmdbViews`. */
  views: CmdbView[]
}

export interface BaselineDiff {
  gone: Record<string, unknown>[]
  appeared: Record<string, unknown>[]
  changed: Record<string, unknown>[]
  edges_gone: Record<string, unknown>[]
  edges_new: Record<string, unknown>[]
  /** O comparatie taiata tacut se citeste ca "atatea s-au schimbat". */
  truncated: boolean
  limit: number
}

export interface BaselineComparePage {
  baseline: Baseline
  diff: BaselineDiff
  statuses: Pair[]
  environments: Pair[]
  criticality: Pair[]
}

export const cmdbShellQuery = queryOptions({
  queryKey: ['cmdb', 'shell'],
  queryFn: () => apiGet<CmdbShell>('/cmdb'),
})

export function cmdbTableQuery(r: CiTableRequest) {
  return queryOptions({
    queryKey: ['cmdb', 'table', r],
    queryFn: () => apiGet<CiTablePage>(`/cmdb/table?${cmdbTableParams(r).toString()}`),
  })
}

export function cmdbRecordQuery(id: number) {
  return queryOptions({
    queryKey: ['cmdb', 'ci', id],
    queryFn: () => apiGet<CiRecordPage>(`/cmdb/ci/${id}`),
  })
}

/** `id` zero = adaugare; `classId` peste zero stramteaza lista de clase la aia una. */
export function cmdbFormQuery(id: number, classId = 0) {
  return queryOptions({
    queryKey: ['cmdb', 'form', id, classId],
    queryFn: () =>
      apiGet<CiFormPage>(id > 0 ? `/cmdb/form/${id}` : `/cmdb/form?class=${classId}`),
    /* Formularul se deschide intr-un modal si trebuie sa poarte valorile de
       ACUM, nu pe cele citite cand s-a deschis lista prima oara. */
    staleTime: 0,
  })
}

export const cmdbHealthQuery = queryOptions({
  queryKey: ['cmdb', 'health'],
  queryFn: () => apiGet<CmdbHealthPage>('/cmdb/health'),
})

export function cmdbHealthListQuery(kind: string, start: number, length: number) {
  return queryOptions({
    queryKey: ['cmdb', 'health', kind, start, length],
    queryFn: () => apiGet<CmdbHealthList>(`/cmdb/health/${kind}?start=${start}&length=${length}`),
  })
}

export const cmdbBaselinesQuery = queryOptions({
  queryKey: ['cmdb', 'baselines'],
  queryFn: () => apiGet<BaselinesPage>('/cmdb/baselines'),
})

export function cmdbBaselineCompareQuery(id: number) {
  return queryOptions({
    queryKey: ['cmdb', 'baselines', id],
    queryFn: () => apiGet<BaselineComparePage>(`/cmdb/baselines/${id}`),
  })
}

export function cmdbTakeBaseline(fields: PostValueMap) {
  return apiPost<WriteResult & { ci_count?: number; edge_count?: number }>('/cmdb/baselines', fields)
}

export function cmdbRemoveBaseline(id: number) {
  return apiPost<WriteResult>(`/cmdb/baselines/${id}/remove`, {})
}

export function cmdbOwnerOptions(companyId: number) {
  return apiGet<{ status: string; people: Person[] }>(`/cmdb/owner-options/${companyId}`)
}

export function cmdbSearch(sourceCiId: number, term: string) {
  return apiGet<{ hits: { id: number; name: string; class: string }[] }>(
    `/cmdb/search?source_ci_id=${sourceCiId}&q=${encodeURIComponent(term)}`,
  )
}

export function cmdbSave(fields: PostValueMap) {
  return apiPost<WriteResult>('/cmdb/save', fields)
}

export function cmdbRemove(id: number) {
  return apiPost<WriteResult>(`/cmdb/ci/${id}/remove`, {})
}

/**
 * `Cmdb::verifyCi` - un om spune ca elementul mai e adevarat.
 *
 * Singura fapta care scoate de pe lista "Unconfirmed" un serviciu de business:
 * el nu e vazut niciodata de niciun releu, deci nu poate imbatrani decat pana
 * se uita cineva la el. Fara butonul asta, lista aia crestea la nesfarsit.
 */
export function cmdbVerify(id: number) {
  return apiPost<WriteResult & { verifiedAt?: string }>(`/cmdb/ci/${id}/verify`, {})
}

export function cmdbAddRelation(sourceCiId: number, targetCiId: number, relationTypeId: number) {
  return apiPost<WriteResult>('/cmdb/relations', {
    source_ci_id: sourceCiId,
    target_ci_id: targetCiId,
    relation_type_id: relationTypeId,
  })
}

export function cmdbRemoveRelation(relationId: number) {
  return apiPost<WriteResult>(`/cmdb/relations/${relationId}/remove`, {})
}

/* ------------------------------------------------------------------------ *
 * Import - `Cmdb::import` / `importPreview` / `importRun` / `importTemplate`
 * ------------------------------------------------------------------------ */

/** Un rand refuzat, cu numarul pe care il vede omul in fisierul lui. */
export interface ImportRefusal {
  row: number
  name: string
  reason: string
}

/** Un rand primit, asa cum va fi scris. */
export interface ImportRow {
  row: number
  name: string
  class_name: string
  company_name: string
  criticality: number | null
  status: number
  serial: string
  part_number: string
}

export interface CmdbImportShell {
  /** camp -> eticheta din antet, in ordinea sablonului */
  columns: Record<string, string>
  classes: CiClass[]
  companies: { id: number; name: string }[]
  maxRows: number
  maxBytes: number
  extensions: string[]
  /** 0 = se vede peste toti clientii; altfel firma pe care sta omul */
  lockedCompany: number
}

export interface ImportPreview {
  status: string
  message?: string
  valid: number
  invalid: ImportRefusal[]
  truncated: boolean
  maxRows: number
  preview: ImportRow[]
}

export interface ImportOutcome {
  status: string
  message?: string
  stats?: { created: number; skipped: number }
  invalid?: ImportRefusal[]
  truncated?: boolean
}

export const cmdbImportShellQuery = queryOptions({
  queryKey: ['cmdb', 'import'],
  queryFn: () => apiGet<CmdbImportShell>('/cmdb/import'),
})

/**
 * Prima trecere: se citeste si se socoteste, NU se scrie.
 *
 * Fisierul pleaca de doua ori, si a doua oara nu se vede: serverul nu tine
 * randurile intre cele doua cereri (v4 n-are sesiune), iar o lista de randuri
 * „deja incuviintate" trimisa inapoi din browser ar fi fost o afirmatie a
 * clientului despre ce e voie sa se scrie - adica exact ce nu poate fi.
 */
export function cmdbImportPreview(file: File) {
  const body = new FormData()
  body.append('file', file)
  return apiPostForm<ImportPreview>('/cmdb/import/preview', body)
}

/** A doua trecere: se citeste iar, se verifica iar, si se scrie. */
export function cmdbImportRun(file: File) {
  const body = new FormData()
  body.append('file', file)
  return apiPostForm<ImportOutcome>('/cmdb/import/run', body)
}

/** Sablonul gol, salvat pe disc. */
export async function cmdbImportTemplate(): Promise<void> {
  const out = await apiExchange('/cmdb/import/template', { method: 'GET' })
  const blob = new Blob([out.bytes], { type: out.type || 'application/octet-stream' })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = out.filename || 'ci_import_template.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

/* ------------------------------------------------------------------------ *
 * Filele randului desfacut - `Cmdb::rowSyslog` / `rowProblems`
 * ------------------------------------------------------------------------ */

/** O linie de syslog, cu tot ce hotaraste ecranul hotarat deja pe server. */
export interface SyslogEvent {
  id: number
  /** Ceasul ECHIPAMENTULUI cand il are, al nostru altfel - `received` spune care. */
  at: string
  received: boolean
  severity: number | null
  severityLabel: string
  severityColour: string
  facility: string
  mnemonic: string
  object: string
  message: string
  /** Linia nedesfacuta se arata bruta: e tot ce avem, si e mai bine ca un rand gol. */
  parsed: boolean
}

export interface SyslogFilters {
  days: number
  severity: string
  facility: string
  object: string
  q: string
}

export interface CiSyslogPanel {
  ciId: number
  hasTable: boolean
  filters: SyslogFilters
  events: SyslogEvent[]
  facilities: { facility: string; total: number }[]
  objects: { object: string; total: number; last_seen: string | null }[]
  severities: Pair[]
  limit: number
}

export interface CiProblem {
  id: number
  title: string
  workaround: string
  status: number
  statusLabel: string
  statusColour: string
  incidents: number
}

export interface CiProblemsPanel {
  ciId: number
  hasTables: boolean
  problems: CiProblem[]
  /** Incidente in serie si NICIO problema deschisa - starea pe care fila exista s-o arate. */
  pattern: { incidents: number; firstSeen: string; lastSeen: string } | null
  statuses: Pair[]
  colours: Record<string, string>
}

export function cmdbSyslogQuery(id: number, filters: Partial<SyslogFilters>) {
  const q = new URLSearchParams()
  if (filters.days) q.set('days', String(filters.days))
  if (filters.severity) q.set('severity', filters.severity)
  if (filters.facility) q.set('facility', filters.facility)
  if (filters.object) q.set('object', filters.object)
  if (filters.q) q.set('q', filters.q)
  const search = q.toString()
  return queryOptions({
    queryKey: ['cmdb', 'ci', id, 'syslog', search],
    queryFn: () => apiGet<CiSyslogPanel>(`/cmdb/ci/${id}/syslog${search === '' ? '' : '?' + search}`),
  })
}

/* ------------------------------------------------------------------------ *
 * Fila Changes - `Cmdb::rowChanges` + `row_changes.php`
 * ------------------------------------------------------------------------ */

/** Un rand din firul "ce s-a intamplat": drift, legatura sau syslog, amestecate. */
export interface CiChangeLog {
  kind: 'change' | 'link' | 'syslog'
  at: string
  what: string
  /** Sub "what": severitatea (syslog) sau "seen N×, M% sure" (legatura). */
  note: string
  text: string
  /** null cand nu e o schimbare (nu are ce aproba cineva). */
  authorised: boolean | null
  authorisedBy: { label: string; ticket: number } | null
}

export interface CiChangeTicket {
  id: number
  subject: string
  description: string
  status: { name: string; color: string; code: number }
  created: string
  resolved: string | null
  comments: { author: string; created: string; text: string }[]
}

export interface CiChangesPanel {
  ciId: number
  logs: CiChangeLog[]
  tickets: CiChangeTicket[]
}

export function cmdbChangesQuery(id: number) {
  return queryOptions({
    queryKey: ['cmdb', 'ci', id, 'changes'],
    queryFn: () => apiGet<CiChangesPanel>(`/cmdb/ci/${id}/changes`),
  })
}

/* ------------------------------------------------------------------------ *
 * Fila Security - `Cmdb::rowSecurity` + `Ci_exposure::forCi`
 * ------------------------------------------------------------------------ */

/** O statie de sub element, pe drumul careia ajunge un CVE. */
export interface ExposedDevice {
  ciId: number
  name: string
  /** 1 = productie. Numai productia primeste eticheta: una pe fiecare statie ar fi facut coloana ilizibila. */
  environment: number
}

/** Un CVE care ajunge la element, cu programul si drumul. */
export interface ExposedCve {
  id: string
  severity: string
  severityLabel: string
  severityColour: string
  /** Nota, ca text: `base_score` e zecimal, si o rotunjire aici ar fi alta cifra decat pe NVD. */
  score: string | null
  /** Primele trei programe care il aduc. */
  products: string[]
  /** Primele doua statii pe care se vede. */
  devices: ExposedDevice[]
  /** Cate statii mai sunt peste cele doua aratate. */
  more: number
  /** Atinge macar o masina de productie - cifra dupa care se hotaraste ce se face in ora urmatoare. */
  production: boolean
}

export interface CiSecurityPanel {
  ciId: number
  /** `it_cve_list` lipseste = baza de vulnerabilitati n-a fost adusa niciodata. */
  hasCveTable: boolean
  devicesTotal: number
  devicesChecked: number
  /** S-au cercetat mai putine statii decat sunt: numaratoarea e PARTIALA, si se spune. */
  partial: boolean
  /** Sunt mai multe CVE-uri decat incap in lista. */
  truncated: boolean
  totalCves: number
  productionCves: number
  counts: Record<string, number>
  cves: ExposedCve[]
  /** Cat de proaspata e baza din care s-a raspuns - `Services\Cve\SyncState::line()`. */
  freshness: { tone: string; text: string; hint: string }
  /** Aparat descoperit, fara agent: nu are programe pe care sa se potriveasca CVE-uri. */
  agentless?: boolean
  /** Ce a gasit scanarea deschis pe el (numai pentru `agentless`). */
  scanPorts?: ScanPort[]
}

export function cmdbSecurityQuery(id: number) {
  return queryOptions({
    queryKey: ['cmdb', 'ci', id, 'security'],
    queryFn: () => apiGet<CiSecurityPanel>(`/cmdb/ci/${id}/security`),
  })
}

export function cmdbProblemsQuery(id: number) {
  return queryOptions({
    queryKey: ['cmdb', 'ci', id, 'problems'],
    queryFn: () => apiGet<CiProblemsPanel>(`/cmdb/ci/${id}/problems`),
  })
}

/* ------------------------------------------------------------------------ *
 * Provenienta si cuiul - `Cmdb::pinAttribute` + `_provenance.php`
 * ------------------------------------------------------------------------ */

/** Un rand din „Who says so": ce atribut, cine l-a pus, si daca e aparat. */
export interface ProvenanceRow {
  attribute: string
  label: string
  /** 0 = nu se stie de unde vine; altfel `Ci_attribute_source::SOURCE_*`. */
  source: number
  /** Culoarea spune UN lucru: om sau masina. Cinci culori ar cere o legenda. */
  human: boolean
  sourceLabel: string
  setAt: string | null
  pinned: boolean
  /** Ce a vrut descoperirea sa puna peste o valoare tinuta, cand chiar difera. */
  blocked: string | null
}

export interface ProvenanceCard {
  hasTable: boolean
  rows: ProvenanceRow[]
  note: string
}

/** Bate sau scoate cuiul. Cere dreptul de SCRIS - schimba ce face masina maine. */
export function cmdbPinAttribute(id: number, attribute: string, pinned: boolean) {
  return apiPost<WriteResult & { pinned?: boolean }>(`/cmdb/ci/${id}/pin`, {
    attribute,
    pinned: pinned ? 1 : 0,
  })
}

/* ------------------------------------------------------------------------ *
 * Cat costa - `Service_costs::forCi()`
 * ------------------------------------------------------------------------ */

/** Costul serviciului pe care elementul IL REPREZINTA. */
export interface OwnCost {
  catalogId: number
  published: boolean
  templateName: string | null
  amount: number
  currency: string
  counts: { ci: number; user: number; ticket: number }
}

/** Cat duce elementul in costul unui serviciu pe care il SUSTINE. */
export interface PartCost {
  serviceCiId: number
  serviceName: string
  /** `null` = serviciul n-are nicio regula pe element, deci elementul nu duce nimic. */
  amount: number | null
  currency: string | null
}

export interface CiCosts {
  own: OwnCost | null
  part: PartCost[]
}

/* ------------------------------------------------------------------------ *
 * Ce a vazut releul - `Ci_telemetry` + `row_telemetry.php`
 * ------------------------------------------------------------------------ */

/** Un port pe care asculta masina. Marcajele vin gata puse de server. */
export interface CiListener {
  id: number
  port: number
  proto: string
  /** Numele serviciului, sau programul cand serviciul nu are nume; `null` = nici unul. */
  service: string | null
  address: string
  /** 0.0.0.0 sau :: - pe toate interfetele, deci si spre alte segmente. */
  everywhere: boolean
  /** Port de administrare sau de partajare care merita o a doua privire (numele lui), sau `null`. */
  watch: string | null
  seen: string | null
}

/** O pereche (partener, port), cu cate observatii a adunat. */
export interface CiFlow {
  direction: string
  inbound: boolean
  /** Cu nume e o dependenta; fara nume e doar o adresa. */
  peerId: number | null
  peerName: string | null
  remoteIp: string
  remotePort: number
  proto: string
  application: string | null
  seen: number
  lastSeen: string | null
}

/* ------------------------------------------------------------------------ *
 * Ce a gasit scanarea - `DiscoveredCiCard`, numai pentru `ref_type` 2
 * ------------------------------------------------------------------------ */

/** Un port vazut de scanare, cu amprenta serviciului cand o are. */
export interface ScanPort {
  port: number
  proto: string
  service: string
  banner: string
  /** Port de administrare sau de partajare: merita o a doua privire. */
  watch: string | null
}

export interface ScanIdentity {
  discoveredId: number | null
  hostname: string | null
  ip: string | null
  mac: string | null
  manufacturer: string | null
  os: string | null
  /** Sistemul e ghicit de agent din TTL sau din porturi, nu citit de pe aparat. */
  osGuessed: boolean
  kind: string | null
  ttl: number | null
  pingMs: number | null
  relayId: number | null
  relayName: string | null
  firstSeen: string | null
  lastSeen: string | null
  virtualHost: string | null
}

export interface ScanRun {
  runId: number
  at: string | null
  type: string
  state: string
  outcome: string | null
  hostname: string | null
  ip: string | null
  ttl: number | null
  pingMs: number | null
}

/** Un flux raportat de agentul ALTEI masini catre / dinspre aparat. */
export interface ScanPeer {
  ciId: number
  ciName: string
  direction: string
  usesIt: boolean
  port: number
  proto: string
  application: string | null
  seen: number
  lastSeen: string | null
}

export interface CiScan {
  /** false = aparatul a fost sters din Discovered; ce se stia vine din `attrs`. */
  found: boolean
  note: string
  identity: ScanIdentity
  ports: ScanPort[]
  hints: string[]
  /** Ce a raspuns aparatul la scanarea de servicii (SNMP sysDescr...). */
  services: string
  history: ScanRun[]
  peers: ScanPeer[]
}

export interface CiTelemetry {
  /** `null` = releul n-a raportat NICIODATA pentru elementul asta. */
  lastReport: string | null
  listeners: CiListener[]
  flows: CiFlow[]
  /** Totalul NUMARAT, nu cel afisat: peste `flowLimit`, lista e taiata. */
  flowTotal: number
  flowLimit: number
}
